import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles, Save, Send, History as HistoryIcon, Image as ImageIcon,
  Palette, ArrowRight, ArrowLeft as ArrowLeftIcon, Copy, X,
} from "lucide-react";

// Real palette pulled from /app/frontend/src/index.css (the source of truth).
const BRAND_COLORS = [
  { name: "Dash Green",        hex: "#22c55e", role: "Primary CTAs · brand identity" },
  { name: "Dash Green (dark)", hex: "#166534", role: "Button shadow · pressed state" },
  { name: "Dash Amber",        hex: "#f59e0b", role: "Rewards · accents · highlights" },
  { name: "Dash Amber (dark)", hex: "#b45309", role: "Amber button shadow" },
  { name: "Trust Navy",        hex: "#0f172a", role: "Headlines · dark sections" },
  { name: "Ink",               hex: "#1e293b", role: "Body text" },
  { name: "Surface White",     hex: "#ffffff", role: "Cards · backgrounds" },
  { name: "Alert Red",         hex: "#ef4444", role: "Warnings · destructive actions" },
];

const BRAND_FONTS = [
  { name: "Fredoka", role: "Headings · brand name (font-fredoka)" },
  { name: "Inter",   role: "Body copy (default)" },
];

const STEPS = [
  {
    key: "welcome",
    title: "Welcome to the Website Editor",
    body: "This is your Wix-style control room for everything on the public site — words, images, buttons, and links. The customer app (orders, payments, schedules) stays in Operations Admin — you don't have to worry about it.",
    icon: Sparkles,
    accent: "bg-amber-100 text-amber-800",
  },
  {
    key: "save",
    title: "Save Draft",
    body: "Every change you make is local until you click Save Draft. Drafts are private — visitors keep seeing the published version until you say so. The button shows a counter when you have unsaved edits.",
    icon: Save,
    accent: "bg-blue-100 text-blue-800",
    visualBtn: { label: "Save draft (3)", style: "btn-push-outline" },
  },
  {
    key: "publish",
    title: "Publish",
    body: "When the drafts look good, click Publish. The changes go live on the public site immediately and a snapshot is automatically saved to history. The marketing copy you change here never affects the actual prices customers are charged — those stay in Operations Admin.",
    icon: Send,
    accent: "bg-green-100 text-green-800",
    visualBtn: { label: "Publish", style: "btn-push-green" },
  },
  {
    key: "revisions",
    title: "Revisions (undo any publish)",
    body: "Made a mistake? Click Revisions to see every past publish — restore any snapshot back into drafts, review it, then re-publish. You can never lose work.",
    icon: HistoryIcon,
    accent: "bg-purple-100 text-purple-800",
    visualBtn: { label: "Revisions", style: "btn-push-outline" },
  },
  {
    key: "media",
    title: "Media library",
    body: "Upload images once (logo, hero photo, anything) and reuse them across pages. From the sidebar click Media → upload → copy the storage path → paste it into any Image field.",
    icon: ImageIcon,
    accent: "bg-pink-100 text-pink-800",
  },
  {
    key: "brand",
    title: "Brand palette & fonts",
    body: "Match these whenever you upload images or pick alt copy. Click any hex code to copy it.",
    icon: Palette,
    accent: "bg-slate-100 text-slate-800",
    isBrand: true,
  },
];

function storageKey(userId) {
  return `bd_editor_tour_seen_${userId || "anon"}`;
}

export default function WelcomeTour() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Show tour for content_editor, superadmin, parent on first /editor visit
    if (!["content_editor", "superadmin", "parent"].includes(user.role)) return;
    const seen = localStorage.getItem(storageKey(user.id));
    if (!seen) {
      // Slight delay so the editor layout renders first
      const t = setTimeout(() => setOpen(true), 350);
      return () => clearTimeout(t);
    }
  }, [user]);

  const dismiss = () => {
    if (user?.id) localStorage.setItem(storageKey(user.id), "1");
    setOpen(false);
  };
  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };
  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  // Manual "show again" entry point: window event triggered from sidebar link
  useEffect(() => {
    const handler = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener("bd:open-editor-tour", handler);
    return () => window.removeEventListener("bd:open-editor-tour", handler);
  }, []);

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="editor-tour"
      onClick={dismiss}
    >
      <div
        className="card-clay max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`editor-tour-step-${s.key}`}
      >
        {/* Top bar */}
        <div className="px-5 pt-5 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Step {step + 1} of {STEPS.length}
          </span>
          <button
            onClick={dismiss}
            className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-500"
            data-testid="editor-tour-skip"
          >
            <X size={16}/>
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-5 mt-2 flex items-center gap-1">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full flex-1 transition ${i <= step ? "bg-green-500" : "bg-slate-200"}`}
            />
          ))}
        </div>

        <div className="px-5 pt-5 pb-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${s.accent}`}>
            <Icon strokeWidth={2.5}/>
          </div>
          <h2 className="font-fredoka text-2xl font-bold text-slate-900 mt-3" data-testid={`editor-tour-title`}>
            {s.title}
          </h2>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.body}</p>

          {/* Step-specific visual */}
          {s.visualBtn && (
            <div className="mt-4 p-4 rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
              <button
                disabled
                className={`${s.visualBtn.style} px-4 py-2 text-xs inline-flex items-center gap-2 cursor-default`}
              >
                <Icon size={14}/> {s.visualBtn.label}
              </button>
            </div>
          )}

          {s.isBrand && <BrandPanel />}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-2 flex items-center justify-between gap-2">
          <button
            onClick={prev}
            disabled={step === 0}
            className="btn-push-outline px-3 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-30"
            data-testid="editor-tour-back"
          >
            <ArrowLeftIcon size={12}/> Back
          </button>
          <button
            onClick={next}
            className="btn-push-green px-4 py-2 text-xs inline-flex items-center gap-1"
            data-testid="editor-tour-next"
          >
            {step === STEPS.length - 1 ? "Got it — let's go" : "Next"} <ArrowRight size={12}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function BrandPanel() {
  const copy = (hex, label) => {
    navigator.clipboard?.writeText(hex);
    toast.success(`Copied ${label} (${hex})`);
  };
  return (
    <div className="mt-3 space-y-3" data-testid="editor-tour-brand">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Brand colors</div>
        <div className="grid grid-cols-1 gap-1.5">
          {BRAND_COLORS.map((c) => (
            <button
              key={c.hex}
              onClick={() => copy(c.hex, c.name)}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 border-2 border-slate-100 text-left"
              data-testid={`brand-color-${c.hex.slice(1)}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex-shrink-0 border border-slate-200"
                style={{ backgroundColor: c.hex }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-fredoka font-bold text-sm text-slate-900">{c.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{c.role}</div>
              </div>
              <div className="flex items-center gap-1 text-[11px] font-mono text-slate-700">
                {c.hex.toUpperCase()}
                <Copy size={10} className="text-slate-400"/>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Brand typography</div>
        <div className="space-y-1.5">
          {BRAND_FONTS.map((f) => (
            <div key={f.name} className="p-2 rounded-xl border-2 border-slate-100">
              <div className="font-fredoka font-bold text-sm text-slate-900">{f.name}</div>
              <div className="text-[10px] text-slate-500">{f.role}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="p-3 rounded-xl bg-amber-50 border-2 border-amber-200 text-[11px] text-amber-900">
        <b>Voice:</b> friendly · kid-led · trustworthy for adults. Short sentences. No emoji blocks. Lowercase casual but never sloppy.
      </div>
    </div>
  );
}
